import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Pill } from "../lib/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DiscoverFromUrlCard } from "../components/DiscoverFromUrlCard";

function fmtUsd(n) {
  if (n === null || n === undefined) return "—";
  return `$${Number(n).toFixed(4)}`;
}
function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${Math.round(Number(n) * 100)}%`;
}
function fmtCredits(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function CreditLedgerPanel({ ledger, error, onRefresh, refreshing, onUpdateBalance }) {
  const [showForm, setShowForm] = useState(false);
  const [newBalance, setNewBalance] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);

  if (error) {
    return (
      <div className="bo-card" data-testid="cc-credit-card">
        <div className="bo-eyebrow">Emergent Credit Ledger</div>
        <h2 className="bo-card-title">Credits</h2>
        <p className="bo-card-lede" data-testid="cc-credit-error">Unable to read ledger: {error}</p>
      </div>
    );
  }
  if (!ledger) {
    return (
      <div className="bo-card" data-testid="cc-credit-card">
        <div className="bo-eyebrow">Emergent Credit Ledger</div>
        <h2 className="bo-card-title">Credits</h2>
        <p className="bo-card-lede">Loading…</p>
      </div>
    );
  }

  const summary = ledger.summary || {};
  const hasCheckpoint = summary.last_known_credits !== null && summary.last_known_credits !== undefined;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitErr(null);
    const value = parseFloat(newBalance);
    if (Number.isNaN(value) || value < 0) {
      setSubmitErr("Enter a non-negative number of credits.");
      return;
    }
    setSubmitting(true);
    try {
      await onUpdateBalance({ credits_balance: value, note: note.trim() || null });
      setNewBalance("");
      setNote("");
      setShowForm(false);
    } catch (err) {
      setSubmitErr(err.message || "Failed to record checkpoint.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bo-card" data-testid="cc-credit-card">
      <div className="bo-eyebrow">
        Emergent Credit Ledger{hasCheckpoint && ` · updated ${fmtDate(summary.checkpoint_recorded_at)}`}
      </div>
      <h2 className="bo-card-title" data-testid="cc-credit-balance">
        {hasCheckpoint ? fmtCredits(summary.last_known_credits) : "Not set"}
        <span style={{ color: "var(--bo-muted)", fontSize: 16, marginLeft: 8 }}>credits</span>
      </h2>
      <p className="bo-card-lede" data-testid="cc-credit-since">
        {hasCheckpoint ? (
          <>
            Since your last balance snapshot, real LLM spend:
            {" "}<strong>{fmtUsd(summary.usd_spent_since_checkpoint)}</strong>.
          </>
        ) : (
          <>Add your current Emergent balance so I can track deltas alongside USD spend.</>
        )}
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          marginTop: 10,
        }}
      >
        <div style={{
          background: "var(--bo-panel-alt)",
          border: "1px solid var(--bo-line)",
          borderRadius: "var(--bo-radius-sm)",
          padding: "10px 12px",
        }} data-testid="cc-credit-today">
          <div style={{ fontSize: 11, color: "var(--bo-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>
            Today (UTC)
          </div>
          <div className="bo-serif" style={{ fontSize: 22, marginTop: 4 }}>
            {fmtUsd(summary.usd_spent_today)}
          </div>
        </div>
        <div style={{
          background: "var(--bo-panel-alt)",
          border: "1px solid var(--bo-line)",
          borderRadius: "var(--bo-radius-sm)",
          padding: "10px 12px",
        }} data-testid="cc-credit-lifetime">
          <div style={{ fontSize: 11, color: "var(--bo-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>
            Lifetime
          </div>
          <div className="bo-serif" style={{ fontSize: 22, marginTop: 4 }}>
            {fmtUsd(summary.usd_spent_lifetime)}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          className="bo-btn bo-btn-secondary"
          onClick={() => setShowForm((s) => !s)}
          data-testid="cc-credit-update-btn"
        >
          {showForm ? "Cancel" : hasCheckpoint ? "Update balance" : "Add balance"}
        </button>
        <button
          type="button"
          className="bo-btn bo-btn-secondary"
          onClick={onRefresh}
          disabled={refreshing}
          data-testid="cc-credit-refresh-btn"
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
        <a
          href="https://emergent.sh/settings/plans"
          target="_blank"
          rel="noreferrer"
          className="bo-btn bo-btn-secondary"
          style={{ textDecoration: "none" }}
          data-testid="cc-credit-manage-link"
        >
          Manage plan ↗
        </a>
      </div>

      {showForm && (
        <form onSubmit={submit} style={{ marginTop: 12 }} data-testid="cc-credit-form">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180 }}>
              <span style={{ fontSize: 12, color: "var(--bo-muted)" }}>Current credits</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newBalance}
                onChange={(e) => setNewBalance(e.target.value)}
                placeholder="e.g. 4882.71"
                className="bo-input"
                data-testid="cc-credit-input-balance"
                required
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 200 }}>
              <span style={{ fontSize: 12, color: "var(--bo-muted)" }}>Note (optional)</span>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. after Slice 2 kickoff"
                className="bo-input"
                data-testid="cc-credit-input-note"
              />
            </label>
            <button
              type="submit"
              className="bo-btn bo-btn-primary"
              disabled={submitting}
              data-testid="cc-credit-submit-btn"
            >
              {submitting ? "Saving…" : "Save checkpoint"}
            </button>
          </div>
          {submitErr && (
            <div className="bo-login-error" role="alert" style={{ marginTop: 8 }}>{submitErr}</div>
          )}
        </form>
      )}

      <p style={{ marginTop: 12, fontSize: 12, color: "var(--bo-muted)" }}>
        Credits reflect your Emergent account balance and are not observable inside the app.
        Update the checkpoint whenever it's convenient — I&apos;ll auto-compute the USD delta from
        <span className="bo-mono"> bos_model_invocations.cost_usd</span> since your last snapshot.
      </p>
    </div>
  );
}

function BudgetPanel({ budget, error }) {
  if (error) {
    return (
      <div className="bo-card" data-testid="cc-budget-card">
        <div className="bo-eyebrow">Model Budget</div>
        <h2 className="bo-card-title">Budget</h2>
        <p className="bo-card-lede" data-testid="cc-budget-error">Unable to read budget: {error}</p>
      </div>
    );
  }
  if (!budget) {
    return (
      <div className="bo-card" data-testid="cc-budget-card">
        <div className="bo-eyebrow">Model Budget</div>
        <h2 className="bo-card-title">Budget</h2>
        <p className="bo-card-lede">Loading…</p>
      </div>
    );
  }

  const stateColors = {
    under_budget: "var(--bo-success)",
    approaching: "var(--bo-danger)",
    reached: "var(--bo-danger)",
  };
  const stateLabels = {
    under_budget: "Under budget",
    approaching: "Approaching ceiling",
    reached: "Ceiling reached",
  };
  const pct = Math.min(1, Math.max(0, budget.percent_used || 0));
  const barColor = stateColors[budget.state] || "var(--bo-accent)";

  return (
    <div className="bo-card" data-testid="cc-budget-card">
      <div className="bo-eyebrow">Model Budget · {budget.date_utc} UTC</div>
      <h2 className="bo-card-title" data-testid="cc-budget-title">
        {fmtUsd(budget.spend_usd)} <span style={{ color: "var(--bo-muted)", fontSize: 20 }}>/ ${budget.ceiling_usd.toFixed(2)}</span>
      </h2>
      <p className="bo-card-lede">
        {budget.calls} LLM {budget.calls === 1 ? "call" : "calls"} today.
        {" "}<span data-testid="cc-budget-state" style={{ color: barColor, fontWeight: 600 }}>
          {stateLabels[budget.state] || budget.state}
        </span>{" "}({fmtPct(budget.percent_used)}).
      </p>
      <div
        aria-label="Daily USD budget usage"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct * 100)}
        style={{
          height: 6,
          borderRadius: 3,
          background: "var(--bo-line)",
          overflow: "hidden",
          marginTop: 4,
        }}
        data-testid="cc-budget-bar"
      >
        <div
          style={{
            width: `${pct * 100}%`,
            height: "100%",
            background: barColor,
            transition: "width 250ms ease, background 250ms ease",
          }}
        />
      </div>
      <p style={{ marginTop: 10, fontSize: 12, color: "var(--bo-muted)" }}>
        Sourced from <span className="bo-mono">bos_model_invocations.cost_usd</span>. Governance uses USD, not credits. [OWNER-05]
      </p>
    </div>
  );
}

function IntelligencePanel({ counts, error }) {
  if (error) {
    return (
      <div className="bo-card" data-testid="cc-intel-card">
        <div className="bo-eyebrow">Gate 2 · Intelligence</div>
        <h2 className="bo-card-title">Intelligence</h2>
        <p className="bo-card-lede" data-testid="cc-intel-error">Unable to read counts: {error}</p>
      </div>
    );
  }
  if (!counts) {
    return (
      <div className="bo-card" data-testid="cc-intel-card">
        <div className="bo-eyebrow">Gate 2 · Intelligence</div>
        <h2 className="bo-card-title">Intelligence</h2>
        <p className="bo-card-lede">Loading…</p>
      </div>
    );
  }
  const tiles = [
    { key: "statements", label: "Statements", n: counts.statements, testId: "cc-count-statements" },
    { key: "proposals_pending", label: "Proposals awaiting review", n: counts.proposals_pending, testId: "cc-count-proposals" },
    { key: "conflicts_open", label: "Open conflicts", n: counts.conflicts_open, testId: "cc-count-conflicts" },
    { key: "jobs_active", label: "Intelligence jobs running", n: counts.jobs_active, testId: "cc-count-jobs" },
  ];
  return (
    <div className="bo-card" data-testid="cc-intel-card">
      <div className="bo-eyebrow">Gate 2 · Intelligence</div>
      <h2 className="bo-card-title">Brand · Product · Audience Brains</h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 12,
          marginTop: 12,
        }}
      >
        {tiles.map((t) => (
          <div
            key={t.key}
            data-testid={t.testId}
            style={{
              background: "var(--bo-panel-alt)",
              border: "1px solid var(--bo-line)",
              borderRadius: "var(--bo-radius-sm)",
              padding: "14px 16px",
            }}
          >
            <div
              className="bo-serif"
              style={{ fontSize: 32, lineHeight: 1, color: "var(--bo-ink)" }}
            >
              {t.n}
            </div>
            <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 6 }}>
              {t.label}
            </div>
          </div>
        ))}
      </div>
      <p style={{ marginTop: 14, fontSize: 12, color: "var(--bo-muted)" }}>
        Slice 1 scaffolds the schema, resolver, budget, and job runner. Slice 2 lights up the Brand Brain
        editor + Review Queue. See the v1.0 spec for the full sequence.
      </p>
    </div>
  );
}

const BAND_TONE = { green: undefined, amber: "warn", red: "danger" };

function ReadinessBar({ label, value }) {
  const pct = Math.round((value || 0) * 100);
  const tone = value >= 0.75 ? "var(--bo-primary, #2e7d5b)" : value >= 0.5 ? "var(--bo-warn, #b57e28)" : "var(--bo-danger, #b34a4a)";
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12, marginTop: 4 }}>
      <span style={{ width: 80, color: "var(--bo-muted)", textTransform: "capitalize" }}>{label}</span>
      <div style={{ flex: 1, background: "var(--bo-line)", borderRadius: 4, height: 6, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: 6, background: tone, transition: "width 200ms" }} />
      </div>
      <span style={{ width: 42, textAlign: "right", fontFamily: "var(--bo-mono)" }}>{pct}%</span>
    </div>
  );
}

function ReadinessCard({ readiness, error }) {
  const [bandFilter, setBandFilter] = useState("all"); // all | attention | red | amber | green
  const [expandedKey, setExpandedKey] = useState(null);
  // Products and Audiences start collapsed to keep the "All" view short.
  // Brand group stays expanded because it's the top-of-funnel context.
  const [collapsedScopes, setCollapsedScopes] = useState({
    brand: false,
    product: true,
    audience: true,
  });

  if (error) {
    return (
      <div className="bo-card" data-testid="cc-readiness-card">
        <div className="bo-eyebrow">Readiness · Slice 6</div>
        <h2 className="bo-card-title">Readiness</h2>
        <p className="bo-card-lede" data-testid="cc-readiness-error">Unable to load readiness: {error}</p>
      </div>
    );
  }
  if (!readiness) {
    return (
      <div className="bo-card" data-testid="cc-readiness-card">
        <div className="bo-eyebrow">Readiness · Slice 6</div>
        <h2 className="bo-card-title">Readiness</h2>
        <p className="bo-card-lede">Loading…</p>
      </div>
    );
  }
  const { reports = [], summary = {} } = readiness;

  // Group by scope, then apply band filter.
  const scopeOrder = ["brand", "product", "audience"];
  const byScope = { brand: [], product: [], audience: [] };
  reports.forEach((r) => {
    if (byScope[r.scope]) byScope[r.scope].push(r);
  });
  scopeOrder.forEach((s) => {
    byScope[s].sort((a, b) => (a.overall || 0) - (b.overall || 0));
  });

  const filterFn = (r) => {
    if (bandFilter === "all") return true;
    if (bandFilter === "attention") {
      const b = (r.band || "").toLowerCase();
      return b === "red" || b === "amber";
    }
    return (r.band || "").toLowerCase() === bandFilter;
  };
  const filteredByScope = {
    brand: byScope.brand.filter(filterFn),
    product: byScope.product.filter(filterFn),
    audience: byScope.audience.filter(filterFn),
  };
  const totalFiltered =
    filteredByScope.brand.length +
    filteredByScope.product.length +
    filteredByScope.audience.length;

  const attentionCount = (summary.red_count || 0) + (summary.amber_count || 0);
  const filterChips = [
    { key: "all", label: `All (${reports.length})`, testId: "cc-readiness-filter-all" },
    { key: "attention", label: `Needs attention (${attentionCount})`, testId: "cc-readiness-filter-attention" },
    { key: "green", label: `Green (${summary.green_count || 0})`, testId: "cc-readiness-filter-green" },
    { key: "amber", label: `Amber (${summary.amber_count || 0})`, testId: "cc-readiness-filter-amber" },
    { key: "red", label: `Red (${summary.red_count || 0})`, testId: "cc-readiness-filter-red" },
  ];

  return (
    <div className="bo-card" data-testid="cc-readiness-card">
      <div className="bo-eyebrow">Readiness · Slice 6</div>
      <h2 className="bo-card-title">
        {summary.reports_count || 0} Brain{(summary.reports_count === 1) ? "" : "s"} · overall {Math.round((summary.overall_avg || 0) * 100)}%
      </h2>

      {/* KPI strip — five compact tiles borrowed from the dashboard mockup */}
      <div
        data-testid="cc-readiness-kpi-strip"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: 8,
          marginTop: 10,
          marginBottom: 4,
        }}
      >
        {[
          {
            label: "Overall",
            value: `${Math.round((summary.overall_avg || 0) * 100)}%`,
            tone: "neutral",
            testId: "cc-kpi-overall",
          },
          {
            label: "Green",
            value: summary.green_count || 0,
            tone: "green",
            testId: "cc-kpi-green",
          },
          {
            label: "Amber",
            value: summary.amber_count || 0,
            tone: "amber",
            testId: "cc-kpi-amber",
          },
          {
            label: "Red",
            value: summary.red_count || 0,
            tone: "red",
            testId: "cc-kpi-red",
          },
          {
            label: "Missing critical",
            value: summary.total_missing_critical || 0,
            tone: "warn",
            testId: "cc-kpi-missing",
          },
        ].map((kpi) => {
          const toneColor =
            kpi.tone === "green" ? "#2e7d5b" :
            kpi.tone === "amber" ? "#c07a2e" :
            kpi.tone === "red" ? "#b34a4a" :
            kpi.tone === "warn" ? "#b57e28" : "var(--bo-ink)";
          return (
            <div
              key={kpi.label}
              data-testid={kpi.testId}
              style={{
                background: "var(--bo-panel-alt)",
                border: "1px solid var(--bo-line)",
                borderRadius: "var(--bo-radius-sm)",
                padding: "10px 12px",
              }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  color: "var(--bo-muted)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {kpi.label}
              </div>
              <div
                className="bo-serif"
                style={{
                  fontSize: 22,
                  lineHeight: 1.1,
                  marginTop: 4,
                  color: toneColor,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {kpi.value}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
        {summary.total_undisposed_sensitive > 0 && (
          <Pill tone="danger" testId="cc-readiness-undisposed">Undisposed sensitive: {summary.total_undisposed_sensitive}</Pill>
        )}
        {/* "Fill a Brain" quick action — nudges the operator toward the highest-value next step */}
        <Link
          to="/brand-os/interview"
          data-testid="cc-fill-brain-btn"
          className="bo-btn bo-btn-primary"
          style={{
            textDecoration: "none",
            fontSize: 12,
            padding: "6px 14px",
            marginLeft: "auto",
          }}
          title="Jump to the Adaptive Interview and pick any Product or Audience with 0% coverage."
        >
          Fill a Brain →
        </Link>
      </div>

      {reports.length === 0 ? (
        <p style={{ color: "var(--bo-muted)", fontSize: 13, marginTop: 12 }} data-testid="cc-readiness-empty">
          Create a Brand, Product, or Audience to see readiness here.
        </p>
      ) : (
        <>
          {/* Filter chips */}
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              marginTop: 12,
              paddingBottom: 10,
              borderBottom: "1px solid var(--bo-line)",
            }}
            data-testid="cc-readiness-filters"
            role="tablist"
            aria-label="Filter brains by readiness band"
          >
            {filterChips.map((chip) => {
              const active = bandFilter === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setBandFilter(chip.key)}
                  data-testid={chip.testId}
                  role="tab"
                  aria-selected={active}
                  style={{
                    fontSize: 12,
                    padding: "4px 12px",
                    borderRadius: 999,
                    border: "1px solid var(--bo-line)",
                    background: active ? "var(--bo-ink)" : "var(--bo-panel-alt)",
                    color: active ? "var(--bo-panel, #fff)" : "var(--bo-ink)",
                    fontWeight: active ? 600 : 500,
                    cursor: "pointer",
                    transition: "background 150ms ease, color 150ms ease",
                  }}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>

          {/* Scrollable brain list, capped at 480px so Campaign card stays visible */}
          <div
            data-testid="cc-readiness-scroll"
            style={{
              maxHeight: 480,
              overflowY: "auto",
              marginTop: 10,
              marginRight: -4,
              paddingRight: 4,
            }}
          >
            {totalFiltered === 0 ? (
              <p
                data-testid="cc-readiness-filter-empty"
                style={{ color: "var(--bo-muted)", fontSize: 13, textAlign: "center", padding: 20 }}
              >
                No brains match the <strong>{bandFilter}</strong> filter.
              </p>
            ) : (
              scopeOrder.map((scope) => {
                const rows = filteredByScope[scope];
                if (rows.length === 0) return null;
                const collapsed = !!collapsedScopes[scope];
                const scopeLabel = scope.charAt(0).toUpperCase() + scope.slice(1);
                return (
                  <div key={scope} style={{ marginBottom: 12 }} data-testid={`cc-readiness-scope-${scope}`}>
                    <button
                      type="button"
                      onClick={() =>
                        setCollapsedScopes((s) => ({ ...s, [scope]: !s[scope] }))
                      }
                      data-testid={`cc-readiness-scope-toggle-${scope}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        width: "100%",
                        padding: "6px 4px",
                        background: "transparent",
                        border: "none",
                        borderBottom: "1px solid var(--bo-line)",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          width: 12,
                          transform: collapsed ? "rotate(-90deg)" : "rotate(0)",
                          transition: "transform 150ms ease",
                          color: "var(--bo-muted)",
                          fontSize: 11,
                        }}
                      >
                        ▾
                      </span>
                      <span
                        className="bo-eyebrow"
                        style={{ margin: 0, letterSpacing: "0.08em" }}
                      >
                        {scopeLabel} · {rows.length}
                      </span>
                    </button>
                    {!collapsed && (
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {rows.map((r) => (
                          <ReadinessRow
                            key={`${r.scope}-${r.scope_id}`}
                            r={r}
                            expanded={expandedKey === `${r.scope}-${r.scope_id}`}
                            onToggle={() =>
                              setExpandedKey((k) =>
                                k === `${r.scope}-${r.scope_id}` ? null : `${r.scope}-${r.scope_id}`,
                              )
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ReadinessRow({ r, expanded, onToggle }) {
  const pct = Math.round((r.overall || 0) * 100);
  const missingCount = (r.missing_critical_fields || []).length;
  const undisposedCount = (r.undisposed_sensitive_fields || []).length;
  return (
    <div
      data-testid={`cc-readiness-row-${r.scope}-${r.scope_id}`}
      style={{
        borderBottom: "1px solid var(--bo-line)",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`cc-readiness-row-toggle-${r.scope}-${r.scope_id}`}
        aria-expanded={expanded}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto 20px",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "8px 6px",
          background: "transparent",
          border: "none",
          textAlign: "left",
          cursor: "pointer",
          fontSize: 13,
        }}
      >
        <span style={{ fontWeight: 500 }}>
          {r.scope_name}
          {(missingCount > 0 || undisposedCount > 0) && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                color: "var(--bo-muted)",
                fontWeight: 400,
              }}
              className="bo-mono"
            >
              {missingCount > 0 && `· ${missingCount} missing`}
              {undisposedCount > 0 && ` · ${undisposedCount} undisposed`}
            </span>
          )}
        </span>
        <Pill tone={BAND_TONE[r.band]}>{r.band}</Pill>
        <span
          style={{
            fontFamily: "var(--bo-mono)",
            fontSize: 12,
            color: "var(--bo-muted)",
            minWidth: 42,
            textAlign: "right",
          }}
        >
          {pct}%
        </span>
        <span
          style={{
            display: "inline-block",
            transform: expanded ? "rotate(90deg)" : "rotate(0)",
            transition: "transform 150ms ease",
            color: "var(--bo-muted)",
            fontSize: 11,
            textAlign: "center",
          }}
        >
          ▸
        </span>
      </button>

      {expanded && (
        <div
          data-testid={`cc-readiness-row-detail-${r.scope}-${r.scope_id}`}
          style={{
            padding: "6px 6px 12px 6px",
            background: "var(--bo-panel-alt)",
            borderRadius: 4,
            margin: "0 0 6px 0",
          }}
        >
          <ReadinessBar label="coverage" value={r.dimensions?.coverage || 0} />
          <ReadinessBar label="confidence" value={r.dimensions?.confidence || 0} />
          <ReadinessBar label="freshness" value={r.dimensions?.freshness || 0} />
          <ReadinessBar label="disposition" value={r.dimensions?.disposition || 0} />
          {(r.recommendations || []).length > 0 && (
            <ul
              style={{
                marginTop: 10,
                marginBottom: 0,
                paddingLeft: 20,
                fontSize: 12,
                color: "var(--bo-muted)",
              }}
            >
              {r.recommendations.map((rec, i) => (
                <li key={i} data-testid={`cc-readiness-rec-${r.scope}-${i}`}>{rec}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


function SliceLedgerPanel({ ledger, error }) {
  if (error) {
    return (
      <div className="bo-card" data-testid="cc-slice-ledger-card">
        <div className="bo-eyebrow">Slice Learning Ledger</div>
        <h2 className="bo-card-title">Slice cost history</h2>
        <p className="bo-card-lede" data-testid="cc-slice-ledger-error">Unable to read slice ledger: {error}</p>
      </div>
    );
  }
  if (!ledger) {
    return (
      <div className="bo-card" data-testid="cc-slice-ledger-card">
        <div className="bo-eyebrow">Slice Learning Ledger</div>
        <h2 className="bo-card-title">Slice cost history</h2>
        <p className="bo-card-lede">Loading…</p>
      </div>
    );
  }
  const windows = ledger.windows || [];
  return (
    <div className="bo-card" data-testid="cc-slice-ledger-card">
      <div className="bo-eyebrow">Slice Learning Ledger · {ledger.checkpoint_count} checkpoints</div>
      <h2 className="bo-card-title">Slice cost history</h2>
      <p className="bo-card-lede">
        Each row is one owner checkpoint-to-checkpoint window. Credits from your balance snapshots;
        USD from <span className="bo-mono">bos_model_invocations.cost_usd</span>. Predicted-vs-actual
        variance lives in <span className="bo-mono">/app/memory/brand_os/slice_metrics.md</span>.
      </p>
      {windows.length === 0 ? (
        <p style={{ color: "var(--bo-muted)", fontSize: 13 }} data-testid="cc-slice-ledger-empty">
          Record two or more checkpoints (above) to see per-slice deltas here.
        </p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            data-testid="cc-slice-ledger-table"
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 6 }}
          >
            <thead>
              <tr style={{ textAlign: "left", color: "var(--bo-muted)", borderBottom: "1px solid var(--bo-line)" }}>
                <th style={{ padding: "8px 10px 8px 0" }}>Window / label</th>
                <th style={{ padding: "8px 10px" }}>Credits Δ</th>
                <th style={{ padding: "8px 10px" }}>USD Δ</th>
                <th style={{ padding: "8px 10px" }}>LLM calls</th>
                <th style={{ padding: "8px 10px" }}>Recorded</th>
              </tr>
            </thead>
            <tbody>
              {windows.map((w, i) => (
                <tr key={`${w.to_recorded_at}-${i}`} style={{ borderBottom: "1px solid var(--bo-line)" }}
                    data-testid={`cc-slice-window-${i}`}>
                  <td style={{ padding: "8px 10px 8px 0", fontFamily: "var(--bo-mono)", fontSize: 12 }}>
                    {w.label}
                  </td>
                  <td style={{ padding: "8px 10px", fontVariantNumeric: "tabular-nums" }} data-testid={`cc-slice-credits-${i}`}>
                    {w.credits_delta > 0 ? `-${fmtCredits(w.credits_delta)}` : `+${fmtCredits(-w.credits_delta)}`}
                  </td>
                  <td style={{ padding: "8px 10px", fontVariantNumeric: "tabular-nums" }} data-testid={`cc-slice-usd-${i}`}>
                    {fmtUsd(w.usd_delta)}
                  </td>
                  <td style={{ padding: "8px 10px", fontVariantNumeric: "tabular-nums" }}>
                    {w.llm_calls}
                  </td>
                  <td style={{ padding: "8px 10px", color: "var(--bo-muted)" }}>
                    {fmtDate(w.to_recorded_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



// P4 — Campaign create form.
// Previously the UI showed "use POST /api/brand-os/campaigns to create
// one". Customers can't open a terminal. This form posts the minimum
// payload and lets the operator keep going into the intake flow.
function CreateCampaignForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState(null);
  const [productId, setProductId] = useState("");
  const [name, setName] = useState("");
  const [objective, setObjective] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get("/products");
        const list = r.data?.products || r.data || [];
        setProducts(list);
        if (list.length === 1 && !productId) setProductId(list[0].id);
      } catch (e) {
        setErr(formatApiError(e));
      }
    })();
  }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    if (!productId || !name.trim()) {
      setErr("Pick a product and name the campaign.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/campaigns", {
        product_id: productId,
        name: name.trim(),
        objective: objective.trim() || null,
      });
      setName("");
      setObjective("");
      setOpen(false);
      if (onCreated) await onCreated();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div style={{ marginBottom: 12 }}>
        <button
          type="button"
          className="bo-btn"
          data-testid="cc-create-campaign-btn"
          onClick={() => setOpen(true)}
          style={{ padding: "6px 12px", fontSize: 13 }}
        >
          + New campaign
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="cc-create-campaign-form"
      style={{
        marginBottom: 14, padding: 12, borderRadius: 8,
        border: "1px dashed var(--bo-line)",
        background: "var(--bo-panel-alt, #faf8f2)",
        display: "grid", gap: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center" }}>
        <strong style={{ fontSize: 13 }}>New campaign</strong>
        <button
          type="button"
          onClick={() => { setOpen(false); setErr(null); }}
          style={{ background: "none", border: 0, cursor: "pointer",
                   color: "var(--bo-muted)", fontSize: 12 }}
          data-testid="cc-create-campaign-cancel"
        >
          Cancel
        </button>
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        <label style={{ fontSize: 12 }}>Product</label>
        <select
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          data-testid="cc-create-campaign-product"
          style={{ padding: "6px 8px", fontSize: 13 }}
        >
          <option value="">— pick a product —</option>
          {(products || []).map((p) => (
            <option key={p.id} value={p.id}>{p.name || p.id}</option>
          ))}
        </select>
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        <label style={{ fontSize: 12 }}>Campaign name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. The Well — Q4 waitlist growth"
          data-testid="cc-create-campaign-name"
          style={{ padding: "6px 8px", fontSize: 13 }}
        />
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        <label style={{ fontSize: 12 }}>Objective (optional)</label>
        <textarea
          value={objective}
          onChange={(e) => setObjective(e.target.value)}
          placeholder="What does this campaign need to accomplish?"
          rows={3}
          data-testid="cc-create-campaign-objective"
          style={{ padding: "6px 8px", fontSize: 13,
                   fontFamily: "inherit" }}
        />
      </div>
      {err && (
        <div
          data-testid="cc-create-campaign-error"
          style={{ color: "crimson", fontSize: 12 }}
        >
          {typeof err === "string" ? err : err.message || JSON.stringify(err)}
        </div>
      )}
      <div>
        <button
          type="submit"
          disabled={busy}
          data-testid="cc-create-campaign-submit"
          className="bo-btn"
          style={{ padding: "6px 12px", fontSize: 13 }}
        >
          {busy ? "Creating…" : "Create campaign"}
        </button>
      </div>
    </form>
  );
}




function CampaignRow({ campaign }) {
  const [readiness, setReadiness] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefLoaded, setBriefLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genErr, setGenErr] = useState(null);
  const [genStatus, setGenStatus] = useState(null);
  const [status, setStatus] = useState(campaign.status);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/campaigns/${campaign.id}/readiness`);
        if (!cancelled) setReadiness(r.data.readiness);
      } catch (_) {
        if (!cancelled) setReadiness({ overall: null, band: "RED", gate_ready: false });
      }
    })();
    return () => { cancelled = true; };
  }, [campaign.id]);

  const loadBrief = async () => {
    try {
      const r = await api.get(`/campaigns/${campaign.id}/brief`);
      setBrief(r.data.brief || null);
      setBriefLoaded(true);
    } catch (e) {
      setBrief(null);
      setBriefLoaded(true);
    }
  };

  useEffect(() => {
    if (status === "brief_ready" && !briefLoaded) {
      loadBrief();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, briefLoaded]);

  const pollJob = async (jobId) => {
    // Poll every 1.5s up to ~90s. Brief generation typically 5-10s.
    for (let i = 0; i < 60; i++) {
      await new Promise((res) => setTimeout(res, 1500));
      try {
        const r = await api.get(`/intelligence/jobs/${jobId}`);
        const job = r.data.job;
        setGenStatus(job.status);
        if (job.status === "succeeded") {
          return { ok: true, job };
        }
        if (job.status === "failed") {
          return {
            ok: false,
            error: job?.error?.message || job?.error?.code || "Brief generation failed.",
          };
        }
      } catch (e) {
        return { ok: false, error: formatApiError(e) };
      }
    }
    return { ok: false, error: "Timed out waiting for brief generation." };
  };

  const generateBrief = async () => {
    setGenerating(true);
    setGenErr(null);
    setGenStatus("queued");
    try {
      const r = await api.post(`/campaigns/${campaign.id}/brief/generate`, { force: true });
      const jobId = r.data.job_id;
      const result = await pollJob(jobId);
      if (!result.ok) {
        setGenErr(result.error);
      } else {
        setStatus("brief_ready");
        setBriefLoaded(false); // triggers reload via effect
        setExpanded(true);
      }
    } catch (e) {
      setGenErr(formatApiError(e));
    } finally {
      setGenerating(false);
      setGenStatus(null);
    }
  };

  const bandColor = readiness?.band === "GREEN"
    ? "#2e7d5b"
    : readiness?.band === "AMBER"
    ? "#c07a2e"
    : "#b34a4a";
  const bandLabel = readiness?.band || "…";
  const pct = readiness?.overall != null ? Math.round(readiness.overall * 100) : null;
  const hasBrief = status === "brief_ready";
  const btnLabel = generating
    ? (genStatus === "running" ? "Generating…" : "Queued…")
    : hasBrief ? "Regenerate brief" : "Generate brief";

  return (
    <div
      data-testid={`cc-campaign-row-${campaign.id}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "10px 12px",
        background: "var(--bo-panel, #fafaf7)",
        border: "1px solid var(--bo-line)",
        borderRadius: 6,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto auto",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{campaign.name}</div>
          <div style={{ fontSize: 11, color: "var(--bo-muted)" }} className="bo-mono">
            {status} · {(campaign.objective || "(no objective set)").slice(0, 60)}
            {campaign.objective && campaign.objective.length > 60 ? "…" : ""}
          </div>
        </div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: bandColor,
            letterSpacing: "0.05em",
          }}
          data-testid={`cc-campaign-band-${campaign.id}`}
        >
          {bandLabel}
        </div>
        <div style={{ fontSize: 12, color: "var(--bo-muted)", minWidth: 42, textAlign: "right" }}>
          {pct != null ? `${pct}%` : "—"}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            className="bo-btn bo-btn-primary"
            onClick={generateBrief}
            disabled={generating}
            data-testid={`cc-campaign-brief-btn-${campaign.id}`}
            title="POST /campaigns/{id}/brief/generate (force=true). Fires an LLM call — cost logged to slice-ledger."
            style={{ fontSize: 12, padding: "4px 10px" }}
          >
            {btnLabel}
          </button>
          {hasBrief && (
            <Link
              to={`/brand-os/campaigns/${campaign.id}`}
              className="bo-btn bo-btn-secondary"
              data-testid={`cc-campaign-open-${campaign.id}`}
              style={{
                fontSize: 12,
                padding: "4px 10px",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Open
            </Link>
          )}
          {hasBrief && (
            <button
              type="button"
              className="bo-btn bo-btn-secondary"
              onClick={() => {
                setExpanded((e) => !e);
                if (!briefLoaded) loadBrief();
              }}
              data-testid={`cc-campaign-brief-toggle-${campaign.id}`}
              style={{ fontSize: 12, padding: "4px 10px" }}
            >
              {expanded ? "Hide" : "View"}
            </button>
          )}
        </div>
      </div>

      {genErr && (
        <div
          role="alert"
          data-testid={`cc-campaign-brief-error-${campaign.id}`}
          style={{
            fontSize: 12,
            color: "var(--bo-danger, #b34a4a)",
            background: "rgba(179, 74, 74, 0.06)",
            border: "1px solid rgba(179, 74, 74, 0.25)",
            borderRadius: 4,
            padding: "6px 8px",
          }}
        >
          Brief generation failed: {genErr}
        </div>
      )}

      {expanded && hasBrief && (
        <div
          data-testid={`cc-campaign-brief-panel-${campaign.id}`}
          style={{
            fontSize: 12,
            background: "var(--bo-panel-alt)",
            border: "1px solid var(--bo-line)",
            borderRadius: 4,
            padding: 12,
            maxHeight: 340,
            overflowY: "auto",
          }}
        >
          {!briefLoaded ? (
            <span style={{ color: "var(--bo-muted)" }}>Loading brief…</span>
          ) : !brief ? (
            <span style={{ color: "var(--bo-muted)" }}>No brief persisted yet.</span>
          ) : (
            <BriefRenderer brief={brief} />
          )}
        </div>
      )}
    </div>
  );
}

function BriefRenderer({ brief }) {
  // Brief is a structured dict written by services/campaign_brief.py.
  // Render the most-useful fields inline; fall back to JSON for anything unexpected.
  const keys = [
    "strategic_promise",
    "core_insight",
    "primary_takeaway",
    "success_criteria",
    "anti_patterns_to_avoid",
  ];
  const renderVal = (v) => {
    if (v === null || v === undefined) return <em style={{ color: "var(--bo-muted)" }}>—</em>;
    if (typeof v === "string") {
      if (v === "INSUFFICIENT_INTELLIGENCE") {
        return (
          <em style={{ color: "var(--bo-danger, #b34a4a)" }}>
            INSUFFICIENT_INTELLIGENCE — feed the Brains first.
          </em>
        );
      }
      return <span>{v}</span>;
    }
    if (Array.isArray(v)) {
      return (
        <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
          {v.map((item, i) => (
            <li key={i} style={{ marginBottom: 2 }}>{typeof item === "string" ? item : JSON.stringify(item)}</li>
          ))}
        </ul>
      );
    }
    return <pre className="bo-mono" style={{ margin: 0, fontSize: 11, whiteSpace: "pre-wrap" }}>{JSON.stringify(v, null, 2)}</pre>;
  };
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {keys.map((k) =>
        brief[k] !== undefined ? (
          <div key={k}>
            <div className="bo-eyebrow" style={{ marginBottom: 2 }}>{k.replace(/_/g, " ")}</div>
            {renderVal(brief[k])}
          </div>
        ) : null,
      )}
      {/* Any extra keys not in the priority list */}
      {Object.keys(brief)
        .filter((k) => !keys.includes(k))
        .map((k) => (
          <div key={k}>
            <div className="bo-eyebrow" style={{ marginBottom: 2 }}>{k.replace(/_/g, " ")}</div>
            {renderVal(brief[k])}
          </div>
        ))}
    </div>
  );
}


function ManualDownloadRow({ lastRebuild, setLastRebuild, cacheBust, setCacheBust }) {
  const { role } = useAuth();
  const isOwner = role === "owner";
  const gate12Base = "/downloads/Precipice_Brand_OS_Gates_1_and_2_Instruction_Manual.docx";
  const gate1Base = "/downloads/Precipice_Brand_OS_Gate_1_Instruction_Manual.docx";

  // State parents pass down. We ALSO persist to sessionStorage so a rogue
  // HMR reload / SPA re-mount doesn't wipe the "Rebuilt ✓" indicator — the
  // user gets to see confirmation for as long as the tab is open.
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildErr, setRebuildErr] = useState(null);

  useEffect(() => {
    if (lastRebuild || cacheBust) return; // already have state
    try {
      const raw = sessionStorage.getItem("bo:docs:lastRebuild");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.rebuilt_at) {
          setLastRebuild(parsed);
          setCacheBust(new Date(parsed.rebuilt_at).getTime());
        }
      }
    } catch (_) {}
  }, [lastRebuild, cacheBust, setLastRebuild, setCacheBust]);

  const gate12Href = cacheBust ? `${gate12Base}?v=${cacheBust}` : gate12Base;

  const rebuild = async () => {
    setRebuilding(true);
    setRebuildErr(null);
    try {
      const res = await api.post(
        "/governance/docs/rebuild-manual?manual=gates_1_and_2",
        {},
      );
      if (res.data?.ok) {
        const payload = {
          size_bytes: res.data.size_bytes,
          duration_ms: res.data.duration_ms,
          rebuilt_at: res.data.rebuilt_at,
        };
        setLastRebuild(payload);
        setCacheBust(Date.now());
        try {
          sessionStorage.setItem("bo:docs:lastRebuild", JSON.stringify(payload));
        } catch (_) {}
      } else {
        setRebuildErr(res.data?.error || "Rebuild failed.");
      }
    } catch (e) {
      setRebuildErr(formatApiError(e));
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
        <a
          href={gate12Href}
          className="bo-btn bo-btn-primary"
          style={{ textDecoration: "none" }}
          data-testid="cc-docs-gate12-link"
          download
        >
          Gates 1 &amp; 2 Instruction Manual (.docx)
        </a>
        <a
          href={gate1Base}
          className="bo-btn bo-btn-secondary"
          style={{ textDecoration: "none" }}
          data-testid="cc-docs-gate1-link"
          download
        >
          Gate 1 only (.docx)
        </a>
        {isOwner && (
          <button
            type="button"
            className="bo-btn bo-btn-secondary"
            onClick={rebuild}
            disabled={rebuilding}
            data-testid="cc-docs-rebuild-btn"
            title="Regenerate the Gates 1 & 2 DOCX from its markdown source. Deterministic — $0 LLM."
          >
            {rebuilding ? "Rebuilding…" : "Rebuild manual"}
          </button>
        )}
      </div>

      {lastRebuild && (
        <p
          style={{ marginTop: 10, fontSize: 12, color: "var(--bo-muted)" }}
          data-testid="cc-docs-rebuild-status"
        >
          <span style={{ color: "var(--bo-success, #2e7d5b)", fontWeight: 600 }}>Rebuilt ✓</span>{" "}
          {(lastRebuild.size_bytes / 1024).toFixed(1)} KB · {lastRebuild.duration_ms} ms ·
          {" "}{fmtDate(lastRebuild.rebuilt_at)}. Click the primary button above to download the fresh copy.
        </p>
      )}
      {rebuildErr && (
        <p
          role="alert"
          style={{ marginTop: 10, fontSize: 12, color: "var(--bo-danger, #b34a4a)" }}
          data-testid="cc-docs-rebuild-error"
        >
          Rebuild failed: {rebuildErr}
        </p>
      )}
    </>
  );
}


export default function CommandCenter() {
  const { user, activeMembership, role } = useAuth();
  const [org, setOrg] = useState(null);
  const [health, setHealth] = useState(null);
  const [counts, setCounts] = useState(null);
  const [budget, setBudget] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [sliceLedger, setSliceLedger] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [orgErr, setOrgErr] = useState(null);
  const [countsErr, setCountsErr] = useState(null);
  const [budgetErr, setBudgetErr] = useState(null);
  const [ledgerErr, setLedgerErr] = useState(null);
  const [sliceLedgerErr, setSliceLedgerErr] = useState(null);
  const [readinessErr, setReadinessErr] = useState(null);
  const [refreshingLedger, setRefreshingLedger] = useState(false);
  // Hoisted here (not inside <ManualDownloadRow />) so the state survives child
  // re-mounts. HMR / SPA route reconciliation would otherwise wipe local state.
  const [manualLastRebuild, setManualLastRebuild] = useState(null);
  const [manualCacheBust, setManualCacheBust] = useState(0);
  // Gate 3 Slice 3.1/3.2: Campaigns snapshot
  const [campaigns, setCampaigns] = useState(null);
  const [campaignsErr, setCampaignsErr] = useState(null);
  // Two-Track \u00a73 \u00b7 System version (build SHA + milestone)
  const [sysVersion, setSysVersion] = useState(null);
  const [sysVersionErr, setSysVersionErr] = useState(null);

  const refreshLedger = async () => {
    setRefreshingLedger(true);
    setLedgerErr(null);
    try {
      const res = await api.get("/governance/credit-ledger");
      setLedger(res.data.ledger);
      // Refresh slice ledger too — same telemetry source.
      try {
        const sl = await api.get("/governance/slice-ledger");
        setSliceLedger(sl.data);
      } catch (_) {}
      // Refresh budget in parallel too; it reads from same telemetry
      try {
        const b = await api.get("/governance/budget");
        setBudget(b.data.budget);
      } catch (_) {}
    } catch (e) {
      setLedgerErr(formatApiError(e));
    } finally {
      setRefreshingLedger(false);
    }
  };

  const updateBalance = async ({ credits_balance, note }) => {
    try {
      const res = await api.post("/governance/credit-ledger/checkpoint", {
        credits_balance,
        note,
      });
      setLedger(res.data.ledger);
      // Recording a new checkpoint changes windows, so refresh slice ledger too.
      try {
        const sl = await api.get("/governance/slice-ledger");
        setSliceLedger(sl.data);
      } catch (_) {}
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Fire in parallel; each panel handles its own error.
      const results = await Promise.allSettled([
        api.get("/organizations/current"),
        api.get("/health"),
        api.get("/intelligence/counts"),
        api.get("/governance/budget"),
        api.get("/governance/credit-ledger"),
        api.get("/governance/slice-ledger"),
        api.get("/intelligence/readiness"),
        api.get("/campaigns"),
        api.get("/system/version"),
      ]);
      if (cancelled) return;
      const [orgRes, healthRes, countsRes, budgetRes, ledgerRes, sliceLedgerRes, readinessRes, campaignsRes, sysVersionRes] = results;
      if (orgRes.status === "fulfilled") setOrg(orgRes.value.data);
      else setOrgErr(formatApiError(orgRes.reason));
      if (healthRes.status === "fulfilled") setHealth(healthRes.value.data);
      if (countsRes.status === "fulfilled") setCounts(countsRes.value.data);
      else setCountsErr(formatApiError(countsRes.reason));
      if (budgetRes.status === "fulfilled") setBudget(budgetRes.value.data.budget);
      else setBudgetErr(formatApiError(budgetRes.reason));
      if (ledgerRes.status === "fulfilled") setLedger(ledgerRes.value.data.ledger);
      else setLedgerErr(formatApiError(ledgerRes.reason));
      if (sliceLedgerRes.status === "fulfilled") setSliceLedger(sliceLedgerRes.value.data);
      else setSliceLedgerErr(formatApiError(sliceLedgerRes.reason));
      if (readinessRes.status === "fulfilled") setReadiness(readinessRes.value.data);
      else setReadinessErr(formatApiError(readinessRes.reason));
      if (campaignsRes.status === "fulfilled") setCampaigns(campaignsRes.value.data.campaigns || []);
      else setCampaignsErr(formatApiError(campaignsRes.reason));
      if (sysVersionRes.status === "fulfilled") setSysVersion(sysVersionRes.value.data);
      else setSysVersionErr(formatApiError(sysVersionRes.reason));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Shell title="Command Center">
      <div className="bo-eyebrow" data-testid="cc-eyebrow">Gate 3 · Slice 3.2 · Campaign Intelligence</div>
      <h1
        className="bo-serif"
        style={{ fontSize: 40, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 8px" }}
        data-testid="cc-title"
      >
        Welcome back, {user?.display_name?.split(" ")[0] || "friend"}.
      </h1>
      <p style={{ color: "var(--bo-muted)", maxWidth: 640, margin: "0 0 20px 0" }} data-testid="cc-lede">
        Two tabs keep this page short: <strong>Main</strong> is the day-to-day working surface
        (Brains readiness · Campaigns). <strong>Ops</strong> is the governance surface
        (credits · budget · slice ledger · docs · system health).
      </p>

      <Tabs defaultValue="main" className="w-full" data-testid="cc-tabs">
        <TabsList data-testid="cc-tabs-list" className="mb-4">
          <TabsTrigger value="main" data-testid="cc-tab-main">Main</TabsTrigger>
          <TabsTrigger value="ops" data-testid="cc-tab-ops">Ops</TabsTrigger>
        </TabsList>

        <TabsContent value="main" data-testid="cc-tab-main-content">
          <div style={{ marginBottom: 16 }}>
            <ReadinessCard readiness={readiness} error={readinessErr} />
          </div>

          <DiscoverFromUrlCard />

          <div className="bo-card" data-testid="cc-campaigns-card">
            <div className="bo-eyebrow">Gate 3 · Campaign Intelligence</div>
            <h2 className="bo-card-title">Campaigns</h2>
            <CreateCampaignForm onCreated={async () => {
              try {
                const r = await api.get("/campaigns");
                setCampaigns(r.data?.campaigns || []);
                setCampaignsErr(null);
              } catch (e) {
                setCampaignsErr(formatApiError(e));
              }
            }} />
            {campaignsErr ? (
              <p style={{ color: "var(--bo-danger, #b34a4a)", fontSize: 12 }} data-testid="cc-campaigns-error">
                {campaignsErr}
              </p>
            ) : campaigns === null ? (
              <p style={{ color: "var(--bo-muted)", fontSize: 13 }}>Loading campaigns…</p>
            ) : campaigns.length === 0 ? (
              <p style={{ color: "var(--bo-muted)", fontSize: 13 }} data-testid="cc-campaigns-empty">
                No campaigns yet. Click <strong>+ New campaign</strong> above to create one.
              </p>
            ) : (
              <>
                <p style={{ color: "var(--bo-muted)", fontSize: 12, margin: "4px 0 10px" }}>
                  {campaigns.length} campaign{campaigns.length === 1 ? "" : "s"} · click{" "}
                  <span className="bo-mono">Generate brief</span> to fire a Slice 3.2 LLM run
                  (force=true; cost logged to slice ledger).
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {campaigns.slice(0, 6).map((c) => (
                    <CampaignRow key={c.id} campaign={c} />
                  ))}
                  {campaigns.length > 6 && (
                    <p style={{ fontSize: 12, color: "var(--bo-muted)" }}>
                      +{campaigns.length - 6} more
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ops" data-testid="cc-tab-ops-content">
          <div style={{ marginBottom: 16 }}>
            <CreditLedgerPanel
              ledger={ledger}
              error={ledgerErr}
              refreshing={refreshingLedger}
              onRefresh={refreshLedger}
              onUpdateBalance={updateBalance}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <SliceLedgerPanel ledger={sliceLedger} error={sliceLedgerErr} />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <BudgetPanel budget={budget} error={budgetErr} />
            <IntelligencePanel counts={counts} error={countsErr} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <CostAlertsPanel role={role} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <DriftAlarmPanel />
          </div>

          <div className="bo-card" data-testid="cc-org-card">
            <div className="bo-eyebrow">Organization</div>
            <h2 className="bo-card-title" data-testid="cc-org-name">
              {org?.name || activeMembership?.org_name || "—"}
            </h2>
            <p className="bo-card-lede">
              Slug <span className="bo-mono">{org?.slug || activeMembership?.org_slug}</span>
              {" "}· role <span className="bo-pill" data-testid="cc-role-pill"><span className="bo-pill-dot" />{role}</span>
            </p>
            {orgErr && (
              <div className="bo-login-error" role="alert" data-testid="cc-error">{orgErr}</div>
            )}
          </div>

          <div className="bo-card" data-testid="cc-slice-card">
            <div className="bo-eyebrow">What just shipped</div>
            <h2 className="bo-card-title">Slice 1 — Gate 2 Foundations</h2>
            <p className="bo-card-lede">
              Persistent intelligence schema (statements · history · proposals · conflicts · jobs · ingestion),
              deterministic confidence band derivation with authority/lifecycle/freshness independence, USD budget
              governance sourced from <span className="bo-mono">bos_model_invocations.cost_usd</span>, and the
              Gate 2 durable job runner. The legacy Copywriter + Guardian flow was retired on
              2026-10-26; all copy now flows through the governed{" "}
              <Link to="/brand-os/campaigns" data-testid="cc-campaigns-link">Campaigns</Link>{" "}
              → Creative pipeline. Approved assets land in{" "}
              <Link to="/brand-os/library" data-testid="cc-library-link">Asset Library</Link>.
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span className="bo-pill"><span className="bo-pill-dot" />Intelligence schema</span>
              <span className="bo-pill"><span className="bo-pill-dot" />Deterministic confidence (no LLM)</span>
              <span className="bo-pill"><span className="bo-pill-dot" />USD budget ceiling</span>
              <span className="bo-pill"><span className="bo-pill-dot" />Durable job runner</span>
              <span className="bo-pill"><span className="bo-pill-dot" />Freshness policy [OWNER-03]</span>
              <span className="bo-pill"><span className="bo-pill-dot" />Cross-tenant 404 [A-17]</span>
            </div>
          </div>

          <div className="bo-card" data-testid="cc-docs-card">
            <div className="bo-eyebrow">Operator Documentation</div>
            <h2 className="bo-card-title">Instruction manuals</h2>
            <p className="bo-card-lede">
              Hand a new operator either DOCX and they can run Brand OS end-to-end. Manuals are generated
              deterministically from <span className="bo-mono">/app/memory/brand_os/</span> markdown sources —
              no LLM in the loop.
            </p>
            <ManualDownloadRow
              lastRebuild={manualLastRebuild}
              setLastRebuild={setManualLastRebuild}
              cacheBust={manualCacheBust}
              setCacheBust={setManualCacheBust}
            />
            <p style={{ marginTop: 10, fontSize: 12, color: "var(--bo-muted)" }}>
              Gates 1 &amp; 2 covers auth, review, publish, Brand/Product/Audience Brains, Conflict Center,
              Adaptive Interview, Website Ingestion, Extract-from-Source, and Readiness Scoring.
            </p>
          </div>

          <div className="bo-card" data-testid="cc-status-card">
            <div className="bo-eyebrow">System</div>
            <h2 className="bo-card-title">Backend health</h2>
            <p className="bo-card-lede">
              Brand OS module status — confirms the module is loaded and answering on this preview URL.
            </p>
            <pre
              className="bo-mono"
              style={{
                padding: 14,
                background: "var(--bo-panel-alt)",
                border: "1px solid var(--bo-line)",
                borderRadius: "var(--bo-radius-sm)",
                overflow: "auto",
                margin: 0,
              }}
              data-testid="cc-health-json"
            >
{JSON.stringify(health || { checking: true }, null, 2)}
            </pre>
          </div>

          <div className="bo-card" data-testid="cc-build-card">
            <div className="bo-eyebrow">Build &amp; Milestone (Two-Track §3)</div>
            <h2 className="bo-card-title">Deployed baseline</h2>
            <p className="bo-card-lede">
              Every deployed build is traceable back to a source SHA and the latest release-ledger milestone.
              Rollback is <span className="bo-mono">git checkout &lt;tag&gt;</span> plus the milestone&rsquo;s DB snapshot.
            </p>
            {sysVersionErr ? (
              <div className="bo-login-error" role="alert" data-testid="cc-build-err">{sysVersionErr}</div>
            ) : sysVersion ? (
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 14px", fontSize: 13 }}>
                <div style={{ color: "var(--bo-muted)" }}>Build SHA</div>
                <div className="bo-mono" data-testid="cc-build-sha">{sysVersion.build_sha || "—"}</div>
                <div style={{ color: "var(--bo-muted)" }}>Milestone</div>
                <div className="bo-mono" data-testid="cc-build-milestone">
                  {sysVersion.latest_milestone_tag || "—"}
                  {sysVersion.latest_milestone_date ? (
                    <span style={{ color: "var(--bo-muted)", marginLeft: 8 }}>
                      · {sysVersion.latest_milestone_date}
                    </span>
                  ) : null}
                </div>
                <div style={{ color: "var(--bo-muted)" }}>Source</div>
                <div className="bo-mono" style={{ fontSize: 12 }}>{sysVersion.source}</div>
                {sysVersion.latest_milestone_description ? (
                  <>
                    <div style={{ color: "var(--bo-muted)", alignSelf: "start" }}>Notes</div>
                    <div style={{ fontSize: 12, color: "var(--bo-muted)" }}>
                      {sysVersion.latest_milestone_description}
                    </div>
                  </>
                ) : null}
              </div>
            ) : (
              <p style={{ color: "var(--bo-muted)", fontSize: 13 }}>Loading system version…</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Cost Alerts (Ops tab) — owner-only threshold editor + over-budget list.
// ---------------------------------------------------------------------------

function CostAlertsPanel({ role }) {
  const [threshold, setThreshold] = useState(null);
  const [draft, setDraft] = useState("");
  const [alerts, setAlerts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);
  const isOwner = role === "owner";

  const load = async () => {
    setErr("");
    try {
      const [settings, list] = await Promise.all([
        api.get("/organizations/current/settings"),
        api.get("/organizations/current/cost-alerts").catch(() => ({ data: null })),
      ]);
      const t = settings.data?.cost_alert_threshold_usd;
      setThreshold(t);
      setDraft(t != null ? String(t) : "");
      setAlerts(list.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  async function save() {
    setBusy(true);
    setErr("");
    setSaved(false);
    try {
      const val = Number(draft);
      if (!Number.isFinite(val) || val < 0.01 || val > 10000) {
        throw new Error("Enter a number between 0.01 and 10000");
      }
      const r = await api.put("/organizations/current/settings/cost-alert-threshold", {
        threshold_usd: val,
      });
      setThreshold(r.data.cost_alert_threshold_usd);
      setSaved(true);
      // Refresh the alerts list against the new threshold.
      const list = await api.get("/organizations/current/cost-alerts");
      setAlerts(list.data);
    } catch (e) {
      setErr(e.message || formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const dirty = draft !== "" && Number(draft) !== threshold;
  const alertCount = alerts?.alerts?.length ?? 0;
  return (
    <div className="bo-card" data-testid="cc-cost-alerts-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="bo-eyebrow">Cost alerts</div>
          <h2 className="bo-card-title">Daily campaign burn threshold</h2>
        </div>
        <Pill
          tone={alertCount > 0 ? "danger" : undefined}
          testId="cc-cost-alerts-count"
        >
          {alertCount === 0 ? "All quiet" : `${alertCount} over budget`}
        </Pill>
      </div>
      <p className="bo-card-lede">
        A campaign lights up here whenever its LLM burn today (UTC day) crosses
        the threshold. Section costs + concept revisions are both counted.
      </p>

      <div
        style={{
          display: "flex", gap: 10, alignItems: "flex-end",
          marginTop: 12, flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label
            htmlFor="cc-threshold-input"
            style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}
          >
            Threshold (USD)
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}>$</span>
            <input
              id="cc-threshold-input"
              data-testid="cc-threshold-input"
              type="number"
              min="0.01"
              max="10000"
              step="0.01"
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setSaved(false); }}
              disabled={!isOwner || busy}
              style={{
                width: 120,
                padding: "6px 10px",
                fontSize: 13,
                fontFamily: "var(--bo-mono)",
                background: "var(--bo-bg)",
                border: "1px solid var(--bo-line)",
                borderRadius: 4,
                color: "var(--bo-ink)",
              }}
            />
          </div>
        </div>
        <button
          type="button"
          className="bo-btn bo-btn-primary"
          onClick={save}
          disabled={!isOwner || busy || !dirty}
          data-testid="cc-threshold-save-btn"
          style={{ fontSize: 12, padding: "6px 14px" }}
          title={isOwner ? "" : "Owner-only setting."}
        >
          {busy ? "Saving…" : saved && !dirty ? "Saved ✓" : "Save threshold"}
        </button>
        {!isOwner && (
          <span style={{ fontSize: 11, color: "var(--bo-muted)" }} data-testid="cc-threshold-owner-only">
            Owner-only. Current: {fmtUsd(threshold || 0)}
          </span>
        )}
      </div>

      {err && (
        <div
          role="alert"
          data-testid="cc-cost-alerts-error"
          style={{
            marginTop: 10, fontSize: 12, color: "var(--bo-danger, #b34a4a)",
            background: "rgba(179, 74, 74, 0.06)",
            border: "1px solid rgba(179, 74, 74, 0.25)",
            borderRadius: 4, padding: "6px 10px",
          }}
        >
          {err}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <div className="bo-eyebrow" style={{ marginBottom: 6 }}>
          Currently over budget
        </div>
        {alertCount === 0 ? (
          <p
            data-testid="cc-cost-alerts-empty"
            style={{ color: "var(--bo-muted)", fontSize: 13, margin: 0 }}
          >
            Nothing to worry about — every campaign is under the threshold today.
          </p>
        ) : (
          <div style={{ display: "grid", gap: 6 }} data-testid="cc-cost-alerts-list">
            {alerts.alerts.map((a) => (
              <Link
                key={a.campaign_id}
                to={`/brand-os/campaigns/${a.campaign_id}`}
                data-testid={`cc-cost-alert-${a.campaign_id}`}
                style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "center", gap: 12,
                  padding: "8px 12px",
                  background: "rgba(179, 74, 74, 0.06)",
                  border: "1px solid rgba(179, 74, 74, 0.25)",
                  borderRadius: 6,
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {a.campaign_name || a.campaign_id}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}>
                    today {fmtUsd(a.today_cost_usd)} · over by {fmtUsd(a.over_by)}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: "var(--bo-ink)" }}>view →</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Drift Alarm (Ops tab) — rolling similarity index over 30/60/90 day windows.
// Surfaces slow content drift between the tenant's public site and the
// governed Vault, using the read-only value_matcher output. See
// backend: services/genesis/drift_index.py
// ---------------------------------------------------------------------------

function DriftAlarmPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    setBusy(true);
    setErr("");
    try {
      const r = await api.get("/genesis/drift-index?windows=30,60,90");
      setData(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { load(); }, []);

  const direction = data?.trend?.direction || "insufficient_data";
  const trendLabel = {
    drifting_apart: "Drifting apart",
    converging: "Converging",
    stable: "Stable",
    insufficient_data: "Insufficient data",
  }[direction] || direction;
  const trendTone = {
    drifting_apart: "danger",
    converging: "success",
    stable: "info",
    insufficient_data: "muted",
  }[direction] || "muted";
  const toneColor = {
    danger: "#b34a4a",
    success: "#2f7a4b",
    info: "#6b6558",
    muted: "#8a8577",
  }[trendTone];

  return (
    <div className="bo-card" data-testid="cc-drift-alarm-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <div>
          <div className="bo-eyebrow">Drift Alarm</div>
          <h2 className="bo-card-title" data-testid="cc-drift-alarm-title">
            Rolling Vault↔site similarity
          </h2>
        </div>
        <button
          type="button"
          className="bo-btn bo-btn-ghost"
          data-testid="cc-drift-alarm-refresh"
          onClick={load}
          disabled={busy}
        >
          {busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <p className="bo-card-lede" style={{ marginBottom: 12 }}>
        Watches how closely the extractor's proposals from your public site match
        your governed Vault, averaged over rolling windows. Every match tier and
        every similarity score is auditable — no silent drift.
      </p>

      {err && (
        <div className="bo-login-error" role="alert" data-testid="cc-drift-alarm-error">
          {err}
        </div>
      )}

      {data && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 12px",
              borderRadius: 8,
              background: trendTone === "danger"
                ? "rgba(179,74,74,0.06)"
                : trendTone === "success"
                ? "rgba(47,122,75,0.06)"
                : "rgba(0,0,0,0.03)",
              border: `1px solid ${toneColor}33`,
              marginBottom: 16,
            }}
            data-testid="cc-drift-alarm-trend"
          >
            <span
              style={{
                display: "inline-block",
                padding: "3px 10px",
                borderRadius: 999,
                background: toneColor,
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
              data-testid="cc-drift-alarm-trend-pill"
            >
              {trendLabel}
            </span>
            <div style={{ fontSize: 13, color: "#4a4a44" }}>
              30-day vs 90-day similarity delta:{" "}
              <b data-testid="cc-drift-alarm-delta">
                {(data.trend?.delta_30_vs_90 ?? 0) >= 0 ? "+" : ""}
                {(data.trend?.delta_30_vs_90 ?? 0).toFixed(3)}
              </b>{" "}
              · confidence <b>{data.trend?.confidence || "low"}</b>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
              marginBottom: 16,
            }}
            data-testid="cc-drift-alarm-windows"
          >
            {(data.windows || []).map((w) => (
              <div
                key={w.days}
                className="bo-metric"
                style={{ padding: 12, borderRadius: 8, background: "#faf6ee", border: "1px solid rgba(200,161,90,.16)" }}
                data-testid={`cc-drift-alarm-window-${w.days}`}
              >
                <div className="bo-metric-label" style={{ fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: "#8a7a5c" }}>
                  Last {w.days}d
                </div>
                <div className="bo-metric-value" style={{ fontSize: 22, fontWeight: 700, color: "#2b2b26" }}>
                  {(w.avg_partial_similarity ?? 0).toFixed(3)}
                </div>
                <div style={{ fontSize: 11, color: "#6b6558", marginTop: 4 }}>
                  {w.report_count} report{w.report_count === 1 ? "" : "s"} ·{" "}
                  leniency {(w.avg_leniency_ratio ?? 0).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          {data.top_drifting_fields?.length > 0 && (
            <div data-testid="cc-drift-alarm-top-fields">
              <div className="bo-eyebrow" style={{ marginBottom: 8 }}>
                Top drifting fields (short window vs long)
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(0,0,0,.08)", textAlign: "left" }}>
                    <th style={{ padding: "6px 8px", fontWeight: 600 }}>Scope · Field</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600, textAlign: "right" }}>Short avg</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600, textAlign: "right" }}>Long avg</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600, textAlign: "right" }}>Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_drifting_fields.map((row, i) => (
                    <tr
                      key={`${row.scope}-${row.field_path}-${i}`}
                      style={{ borderBottom: "1px solid rgba(0,0,0,.04)" }}
                      data-testid={`cc-drift-alarm-field-${row.scope}-${row.field_path.replace(/\./g, "-")}`}
                    >
                      <td style={{ padding: "6px 8px", fontFamily: "var(--bo-mono, monospace)", fontSize: 12 }}>
                        {row.scope} · {row.field_path}
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{row.avg_similarity_short.toFixed(3)}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{row.avg_similarity_long.toFixed(3)}</td>
                      <td
                        style={{
                          padding: "6px 8px",
                          textAlign: "right",
                          color: row.delta < 0 ? "#b34a4a" : row.delta > 0 ? "#2f7a4b" : "#4a4a44",
                          fontWeight: 600,
                        }}
                      >
                        {row.delta >= 0 ? "+" : ""}
                        {row.delta.toFixed(3)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.matcher_version_stability && !data.matcher_version_stability.stable && (
            <div
              style={{
                marginTop: 12,
                padding: "8px 12px",
                borderRadius: 6,
                background: "rgba(179,74,74,0.06)",
                border: "1px solid rgba(179,74,74,0.25)",
                fontSize: 12,
                color: "#7a2626",
              }}
              data-testid="cc-drift-alarm-version-mixed"
            >
              ⚠︎ Reports in this window span multiple matcher versions:{" "}
              {data.matcher_version_stability.versions_seen.join(" → ")}. Trend
              deltas may reflect matcher changes, not content drift.
            </div>
          )}
        </>
      )}

      {!data && !err && !busy && (
        <div style={{ fontSize: 13, color: "#6b6558", padding: 8 }}>
          Loading drift snapshot…
        </div>
      )}
    </div>
  );
}

